import { array, boolean, number, object, string } from "yup";
import { Data, dateMax, dateSameOrAfter, stringMax, timeSchema } from "./utils";
import {
  AutreAppelCategorie,
  ChargesType,
  TenantLeaseType,
} from "../utils/constants/tenants";

const dateFinBail = () =>
  dateSameOrAfter(
    "debut",
    "La date de fin de bail ne peut pas être antérieure à la date de début",
  );

export class TenantStatus {
  static Actif = new TenantStatus(1);
  static Candidat = new TenantStatus(4);
  static Archive = new TenantStatus(5);
  static CandidatArchive = new TenantStatus(6);

  constructor(status) {
    this.status = status;
  }
}

export class PaymentType {
  static PRELEVEMENT = "prelevement";
  static VIREMENT = "virement";
  static ESPECE = "espece";
  static CHEQUE = "cheque";
}

export class TenantPaymentStatus {
  static A_JOUR = "a_jour";
  static IMPAYE = "impaye";
  static RETARD = "retard";
  static DG_IMPAYE = "dg_impaye";
  static DG_A_JOUR = "dg_a_jour";

  static get(balanceTenant, loyer) {
    let loyerStatus = this.RETARD;
    let dgStatus =
      balanceTenant?.dg_credit + balanceTenant?.dg_debit < 0
        ? this.DG_IMPAYE
        : this.DG_A_JOUR;
    if (balanceTenant?.balance >= 0) {
      loyerStatus = this.A_JOUR;
    } else if (balanceTenant?.balance < -loyer) {
      loyerStatus = this.IMPAYE;
    }

    return [loyerStatus, dgStatus];
  }
}

export class Tenant extends Data {
  autre_appel = undefined;
  letting_fees = undefined;
  charges = undefined;
  creation_date = undefined;
  date_signature = undefined;
  day_payment = undefined;
  debut = undefined;
  deposit = undefined;
  dernier_loyer = undefined;
  duree_contrat = undefined;
  fin = undefined;
  id = undefined;
  id_bien = undefined;
  irl_annee = undefined;
  irl_indice = undefined;
  irl_trimestre = undefined;
  last_revision_date = undefined;
  lease_type = undefined;
  loyer_hc = undefined;
  mandataire_tva = undefined;
  notes = undefined;
  payment_recurrence = undefined;
  payment_periods_from_start_date = undefined;
  preavis = undefined;
  reference_dossier = undefined;
  remuneration_montant = undefined;
  remuneration_type = undefined;
  status = undefined;
  taux_tva = undefined;
  taux_tva_charge = undefined;
  tva = undefined;
  tva_charge = undefined;
  type_charge = undefined;
  type_irl = undefined;
  is_triennal = undefined;
  is_type_quittance = undefined;
  is_indemnite_occupation = undefined;
  echeance_paiement = undefined;
}

export const headerTenantSchema = object({
  id_bien: number().required("Un bien doit obligatoirement être selectionné"),
  nom: string().required("Un nom de profil est obligatoire"),
  reference_dossier: stringMax(250).optional(),
});
export const LETTING_FEES_INITIAL_VALUE = {
  landlord_letting_fee: "",
  tenant_letting_fee: "",
  landlord_inventory_fee: "",
  tenant_inventory_fee: "",
};

const montantHonoraires = () =>
  number()
    .transform((value, original) =>
      original === "" || original === null ? undefined : value,
    )
    .min(0, "Le montant doit être positif")
    .optional();

// Art. 5 loi 89-462 : la part TTC du locataire ne peut excéder celle du bailleur
const partLocataire = (champBailleur) =>
  montantHonoraires().test(
    "part-locataire",
    "La part du locataire ne peut pas dépasser celle du propriétaire",
    function (value) {
      const partBailleur = this.parent?.[champBailleur];
      return (
        value === undefined ||
        partBailleur === undefined ||
        partBailleur === null ||
        partBailleur === "" ||
        value <= Number(partBailleur)
      );
    },
  );

export const lettingFeesSchema = object({
  landlord_letting_fee: montantHonoraires(),
  tenant_letting_fee: partLocataire("landlord_letting_fee"),
  landlord_inventory_fee: montantHonoraires(),
  tenant_inventory_fee: partLocataire("landlord_inventory_fee"),
});

export const convertTenantBaseSchema = object({
  id_bien: string().required("Vous devez selectionner un bien"),
  id_lease_template: number()
    .integer()
    .required("Vous devez selectionner un modèle de bail"),
  loyer_hc: number().required("Le loyer hors charge est obligatoire"),
  charges: number().required("Le montant des charges est obligatoire"),
  irl_trimestre: number().optional(),
  irl_indice: number().optional(),
  irl_annee: number().optional(),
  type_charge: stringMax(250).optional(),
  dernier_loyer: number().optional(),

  debut: dateMax().required("La date de début du bail est obligatoire"),
  duree_contrat: number()
    .integer()
    .min(1, "La durée du contrat doit être supérieure à 0")
    .when(["genBail"], {
      is: (genBail) => genBail === "false",
      then: (s) => s.required("La durée du contrat est obligatoire"),
      otherwise: (s) => s.optional(),
    }),

  date_signature: dateMax().when(["genBail"], {
    is: (genBail) => genBail === "false",
    then: (schema) =>
      schema.required("La date de signature du bail est obligatoire"),
    otherwise: (schema) => schema,
  }),
  genBail: stringMax(250).required(" "),
  generate_initial_balance: boolean(),
  type_envoi: stringMax(250).required(" "),
  payment_recurrence: stringMax(250).required(
    "La récurrence de paiement est obligatoire",
  ),
  payment_periods_from_start_date: boolean().optional(),
  detail_charges: array().of(string()),
  tva_charge: boolean().optional(),
  taux_tva_charge: number().optional(),
  tva: boolean().optional(),
  taux_tva: number().optional(),
  autre_appel: array()
    .of(
      object({
        categorie: string()
          .oneOf(Object.values(AutreAppelCategorie))
          .required("La catégorie est obligatoire"),
        label: string().when("categorie", {
          is: "autre_charge",
          then: () => stringMax(250).required("La description est obligatoire"),
          otherwise: (schema) => schema.notRequired(),
        }),
        montant: number().required("Le montant est obligatoire"),
        tva: boolean(),
        taux_tva: number().nullable().optional(),
        remboursement: boolean(),
      }),
    )
    .optional(),
  fin: dateFinBail(),
  is_meuble: boolean(),
  letting_fees: lettingFeesSchema.optional(),
});

const ACCOUNT_NUMBER_MESSAGE = "1 à 20 lettres ou chiffres, sans espace";
const accountNumberSchema = string()
  .matches(/^[A-Za-z0-9]*$/, ACCOUNT_NUMBER_MESSAGE)
  .max(20, ACCOUNT_NUMBER_MESSAGE)
  .optional();

export const tenantLeaseFormSchema = object({
  loyer_hc: number().required("Le loyer hors charge est obligatoire"),
  montant_caf: number().optional(),
  charges: number().required("Le montant des charges est obligatoire"),
  type_charge: stringMax(250).optional(),
  deposit: number().when(["lease_type"], {
    is: (lease_type) => lease_type !== TenantLeaseType.MOBILITE,
    then: number().required("Le montant du dépôt de garantie est obligatoire"),
    otherwise: number().optional(),
  }),
  tva: boolean().optional(),
  preavis: boolean().optional(),
  lease_type: stringMax(250).optional(),
  irl_trimestre: number().optional(),
  irl_indice: number().optional(),
  irl_annee: number().optional(),
  type_irl: stringMax(250).optional(),
  dernier_loyer: number().optional(),
  payment_recurrence: stringMax(250).required(
    "La récurrence de paiement est obligatoire",
  ),
  payment_periods_from_start_date: boolean().optional(),
  day_payment: number()
    .required("Le jour de paiement du loyer est obligatoire")
    .min(1, "Le jour de paiement du loyer doit être supérieur à 1")
    .max(28, "Le jour de paiement du loyer doit être inférieur à 28"),
  debut: dateMax().required("La date de début du bail est obligatoire"),
  fin: dateFinBail().required("La date de fin du bail est obligatoire"),
  date_signature: dateMax().required("La date de signature est requise"),
  last_revision_date: dateMax().optional(),
  article_specific: string().optional(),
  activite_exerce: string().optional(),
  join_notice: boolean(),
  is_triennal: boolean(),
  is_type_quittance: boolean(),
  is_indemnite_occupation: boolean(),
  echeance_paiement: string().optional(),
  customer_account_number: accountNumberSchema,
  deposit_account_number: accountNumberSchema,
});

export const convertTenantSchema = convertTenantBaseSchema.concat(
  object({
    deposit: number().required(
      "Le montant du dépôt de garantie est obligatoire",
    ),
    day_payment: number()
      .required("Le jour de paiement du loyer est obligatoire")
      .min(1, "Le jour de paiement du loyer doit être supérieur à 1")
      .max(28, "Le jour de paiement du loyer doit être inférieur à 28"),
    date_dernier_loyer: dateMax().optional(),
    nature_dernier_travaux: string().optional(),
    montant_dernier_travaux: number().optional(),
  }),
);
export const convertTenantMobiliteSchema = convertTenantBaseSchema.concat(
  object({
    raison_mobilite: stringMax(250).required(" "),
    day_payment: number()
      .required("Le jour de paiement du loyer est obligatoire")
      .min(1, "Le jour de paiement du loyer doit être supérieur à 1")
      .max(28, "Le jour de paiement du loyer doit être inférieur à 28"),
    fin: dateFinBail().required("La date de fin du bail est obligatoire"),
  }),
);
export const convertTenantCivilSchema = convertTenantBaseSchema.concat(
  object({
    preavis_bailleur: number().when("genBail", {
      is: "true",
      then: number()
        .required("Le préavis bailleur est obligatoire")
        .min(1, "Le nombre doit être supérieur à 0"),
      otherwise: number().notRequired(),
    }),
    preavis_locataire: number().when("genBail", {
      is: "true",
      then: number()
        .required("Le préavis locataire est obligatoire")
        .min(1, "Le nombre doit être supérieur à 0"),
      otherwise: number().notRequired(),
    }),
    echeance_bailleur: stringMax(250).required(" "),
    echeance_locataire: stringMax(250).required(" "),
    day_payment: number()
      .required("Le jour de paiement du loyer est obligatoire")
      .min(1, "Le jour de paiement du loyer doit être supérieur à 1")
      .max(28, "Le jour de paiement du loyer doit être inférieur à 28"),
  }),
);
export const convertTenantLCDSchema = convertTenantBaseSchema.concat(
  object({
    acompte: stringMax(250).required(" "),
    date_paiement_solde: dateMax().when("acompte", {
      is: "false",
      then: dateMax().required("La date de paiement du solde est obligatoire"),
      otherwise: dateMax().notRequired(),
    }),
    pourcentage_acompte: number()
      .integer()
      .when("acompte", {
        is: "false",
        then: (s) => s.notRequired(),
        otherwise: (s) =>
          s.required("Le pourcentage de l'acompte est obligatoire"),
      }),
    type_acompte: string().when("acompte", {
      is: "false",
      then: string().notRequired(),
      otherwise: string().required("Type acompte est obligatoire"),
    }),
    condition_paiement_solde: string().when("type_acompte", {
      is: "condition_specifique",
      then: string().required(
        "La condition spécifique de paiement est obligatoire",
      ),
      otherwise: string().notRequired(),
    }),
    date_limite_paiement_solde: dateMax().when("type_acompte", {
      is: "date_limite",
      then: dateMax().required(
        "La date limite de paiement du solde est obligatoire",
      ),
      otherwise: dateMax().notRequired(),
    }),
    taxe_sejour: number().required("La taxe de séjour est obligatoire"),
    nombre_personne: number().required(
      "Le nombre de personne lors de ce séjour est obligatoire",
    ),
    montant_surcharge: number().required(
      "Le montant de la surcharge par personne est obligatoire",
    ),
    fin: dateFinBail().required("La date de fin du bail est obligatoire"),
    hour_start: timeSchema().required(
      "L'heure de début du bail est obligatoire",
    ),
    hour_end: timeSchema().required("L'heure de fin du bail est obligatoire"),
  }),
);
export const TENANT_INITIAL_VALUE = {
  nom: "",
  id_bien: "",
  id_profile: "",
  loyer_hc: "",
  montant_caf: "",
  dernier_loyer: "",
  charges: "",
  deposit: "",
  status: "1",
  day_payment: "1",
  debut: "",
  fin: "",
  irl_indice: "",
  irl_trimestre: 1,
  irl_annee: "",
  type_charge: ChargesType.PROVISION,
  preavis: false,
  lease_type: "",
  last_revision_date: "",
  type_envoi: "elec",
  date_signature: "",
  payment_recurrence: "1",
  payment_periods_from_start_date: false,
  notes: "",
  article_specific: "",
  acompte: "false",
  date_paiement_solde: "",
  pourcentage_acompte: "",
  type_acompte: "jour_entree",
  condition_paiement_solde: "",
  taxe_sejour: "",
  nombre_personne: "",
  montant_surcharge: "",
  hour_start: "",
  hour_end: "",
  date_dernier_loyer: "",
  nature_dernier_travaux: "",
  montant_dernier_travaux: "",
  reference_dossier: "",
  join_notice: true,
  tva: false,
  taux_tva: "",
  taux_tva_charge: "",
  is_triennal: false,
  is_type_quittance: true,
  is_indemnite_occupation: false,
  echeance_paiement: "",
};

export const ADD_TENANT_INITIAL_VALUE = {
  id_bien: "",
  id_lease_template: "",
  loyer_hc: "",
  dernier_loyer: "",
  charges: "",
  deposit: "",
  status: "1",
  day_payment: "1",
  debut: "",
  fin: "",
  nom: "",
  duree_contrat: "",
  date_signature: "",
  irl_indice: "",
  irl_trimestre: 1,
  irl_annee: "",
  type_charge: ChargesType.PROVISION,
  type_envoi: "elec",
  raison_mobilite: "",
  payment_recurrence: "1",
  payment_periods_from_start_date: false,
  remuneration_type: "",
  remuneration_montant: "",
  detail_charges: [],
  preavis_locataire: "",
  echeance_locataire: "anytime",
  preavis_bailleur: "",
  echeance_bailleur: "anytime",
  is_meuble: false,
  notes: "",
  article_specific: "",
  acompte: "false",
  date_paiement_solde: "",
  date_limite_paiement_solde: "",
  pourcentage_acompte: "",
  type_acompte: "jour_entree",
  condition_paiement_solde: "",
  taxe_sejour: "",
  nombre_personne: "",
  montant_surcharge: "",
  hour_start: "",
  hour_end: "",
  date_dernier_loyer: "",
  nature_dernier_travaux: "",
  montant_dernier_travaux: "",
  join_notice: true,
  clauses_specifiques: [],
  autre_appel: [],
  letting_fees: LETTING_FEES_INITIAL_VALUE,
  tva_charge: false,
  taux_tva_charge: "",
  tva: false,
  taux_tva: "",
  activite_exerce: "",
  is_triennal: false,
  is_type_quittance: true,
  is_indemnite_occupation: false,
  echeance_paiement: "",
};
